// Reusable building blocks for the new-hire onboarding document overlays.
// Each export maps to a class in navigator_people_v83.html:
//   DocumentBanner         -> .people-scope-banner
//   DocumentDetailsCard    -> .people-flow-summary   (Employee / Document / Manager / Due grid)
//   DocumentSection        -> .people-document-section (+ -head)
//   DocumentFormGrid       -> .people-document-form-grid
//   DocumentField/Input    -> .field / .field-label / .field-hint / .input
//   TouchpointSource       -> .people-touchpoint-source
//   DocumentResourceCard   -> .people-document-resource
//   DocumentNoticeCard     -> .people-document-acknowledgement
//   DocumentValidation     -> .people-flow-validation
import React from 'react';
import {
  View,
  Text,
  TextInput,
  Pressable,
  Platform,
  StyleSheet,
  StyleProp,
  ViewStyle,
  TextInputProps,
} from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import FontFamily from '../../../../../constants/fonts';
import { DocColors } from './tokens';

// ─── Banner ───────────────────────────────────────────────────────────────────
export const DocumentBanner: React.FC<{ text: string }> = ({ text }) => (
  <View style={styles.banner}>
    <Text style={styles.bannerText}>{text}</Text>
  </View>
);

// ─── Details card (label/value tiles, two per row) ───────────────────────────
export interface DocumentDetail {
  label: string;
  value: string;
}

export const DocumentDetailsCard: React.FC<{ items: DocumentDetail[] }> = ({ items }) => (
  <View style={styles.detailsGrid}>
    {items.map((item) => (
      <View key={item.label} style={styles.detailTile}>
        <Text style={styles.detailLabel}>{item.label}</Text>
        <Text style={styles.detailValue}>{item.value || '—'}</Text>
      </View>
    ))}
  </View>
);

// ─── Section (title + right-aligned hint, divider, content) ──────────────────
interface DocumentSectionProps {
  title: string;
  hint?: string;
  children?: React.ReactNode;
  style?: StyleProp<ViewStyle>;
}

export const DocumentSection: React.FC<DocumentSectionProps> = ({ title, hint, children, style }) => (
  <View style={[styles.section, style]}>
    <View style={styles.sectionHead}>
      <Text style={styles.sectionTitle}>{title}</Text>
      {!!hint && <Text style={styles.sectionHint}>{hint}</Text>}
    </View>
    {children}
  </View>
);

// ─── Two-column form grid ────────────────────────────────────────────────────
export const DocumentFormGrid: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <View style={styles.formGrid}>{children}</View>
);

// ─── Field (label + control + hint) ──────────────────────────────────────────
interface DocumentFieldProps {
  label: string;
  required?: boolean;
  hint?: string;
  /** Spans both columns of the form grid. */
  fullWidth?: boolean;
  children: React.ReactNode;
}

export const DocumentField: React.FC<DocumentFieldProps> = ({ label, required, hint, fullWidth, children }) => (
  <View style={[styles.field, fullWidth && styles.fieldFull]}>
    <Text style={styles.fieldLabel}>
      {label}
      {required && <Text style={styles.fieldRequired}> *</Text>}
    </Text>
    {children}
    {!!hint && <Text style={styles.fieldHint}>{hint}</Text>}
  </View>
);

export const DocumentInput: React.FC<TextInputProps & { multiline?: boolean }> = ({ style, multiline, editable, ...rest }) => (
  <TextInput
    {...rest}
    multiline={multiline}
    editable={editable}
    placeholderTextColor={DocColors.textMuted}
    style={[styles.input, multiline && styles.textarea, editable === false && styles.inputDisabled, style]}
  />
);

/** yyyy-mm-dd. Uses the browser's native date picker on web (as in the mockup), a text field elsewhere. */
export const DocumentDateInput: React.FC<{
  value: string;
  onChange: (value: string) => void;
  max?: string;
  min?: string;
}> = ({ value, onChange, max, min }) => {
  if (Platform.OS === 'web') {
    return React.createElement('input', {
      type: 'date',
      value,
      min,
      max,
      onChange: (e: any) => onChange(e.target.value),
      style: {
        boxSizing: 'border-box',
        width: '100%',
        height: 32,
        padding: '4px 12px',
        border: `1px solid ${DocColors.inputBorder}`,
        borderRadius: 6,
        backgroundColor: DocColors.surface,
        color: DocColors.textPrimary,
        fontSize: 14,
        lineHeight: '20px',
        fontFamily: 'inherit',
      },
    });
  }
  return <DocumentInput value={value} onChangeText={onChange} placeholder="YYYY-MM-DD" />;
};

// ─── Touchpoint source (blue left rule) ──────────────────────────────────────
export const TouchpointSource: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <View style={styles.touchpoint}>
    <Text style={styles.touchpointTitle}>{title}</Text>
    <Text style={styles.touchpointText}>{text}</Text>
  </View>
);

// ─── Learning resource card (link button + "reviewed" checkbox) ──────────────
interface DocumentResourceCardProps {
  title: string;
  description: string;
  linkLabel: string;
  onOpenLink: () => void;
  checkboxLabel: string;
  checked: boolean;
  onToggle: () => void;
}

export const DocumentResourceCard: React.FC<DocumentResourceCardProps> = ({
  title, description, linkLabel, onOpenLink, checkboxLabel, checked, onToggle,
}) => (
  <View style={styles.resourceCard}>
    <View style={styles.resourceTop}>
      <View style={styles.resourceCopy}>
        <Text style={styles.resourceTitle}>{title}</Text>
        <Text style={styles.resourceDescription}>{description}</Text>
      </View>
      <Pressable style={styles.resourceLink} onPress={onOpenLink}>
        <Text style={styles.resourceLinkText}>{linkLabel}</Text>
      </Pressable>
    </View>
    <Pressable style={styles.checkboxRow} onPress={onToggle} accessibilityRole="checkbox" accessibilityState={{ checked }}>
      <MaterialIcons
        name={checked ? 'check-box' : 'check-box-outline-blank'}
        size={20}
        color={checked ? DocColors.blue700 : DocColors.textSecondary}
      />
      <Text style={styles.checkboxLabel}>{checkboxLabel}</Text>
    </Pressable>
  </View>
);

// ─── Amber notice card (e.g. EHS acknowledgement) ────────────────────────────
export const DocumentNoticeCard: React.FC<{ title: string; text: string }> = ({ title, text }) => (
  <View style={styles.noticeCard}>
    <Text style={styles.noticeTitle}>{title}</Text>
    <Text style={styles.noticeText}>{text}</Text>
  </View>
);

// ─── Validation message ──────────────────────────────────────────────────────
export const DocumentValidation: React.FC<{ message: string }> = ({ message }) => (
  <View style={styles.validation} accessibilityRole="alert">
    <Text style={styles.validationText}>{message}</Text>
  </View>
);

const styles = StyleSheet.create({
  // banner
  banner: {
    borderWidth: 1,
    borderColor: DocColors.blue100,
    backgroundColor: DocColors.blue25,
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  bannerText: {
    fontFamily: FontFamily.medium,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.blue900,
  },

  // details card
  detailsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  detailTile: {
    flexGrow: 1,
    flexBasis: '48%',
    minWidth: 180,
    backgroundColor: DocColors.gray25,
    borderRadius: 8,
    padding: 10,
  },
  detailLabel: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  detailValue: {
    marginTop: 2,
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },

  // section
  section: {
    borderWidth: 1,
    borderColor: DocColors.blue100,
    backgroundColor: DocColors.pageBg,
    borderRadius: 10,
    padding: 16,
  },
  sectionHead: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 14,
    paddingBottom: 9,
    borderBottomWidth: 1,
    borderBottomColor: DocColors.blue100,
  },
  sectionTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 16,
    lineHeight: 22,
    color: DocColors.textPrimary,
  },
  sectionHint: {
    flexShrink: 1,
    textAlign: 'right',
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },

  // form
  formGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    columnGap: 16,
    rowGap: 14,
  },
  field: {
    flexGrow: 1,
    flexBasis: '46%',
    minWidth: 240,
    gap: 8,
  },
  fieldFull: {
    flexBasis: '100%',
  },
  fieldLabel: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },
  fieldRequired: {
    color: DocColors.required,
  },
  fieldHint: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textMuted,
  },
  input: {
    height: 32,
    borderWidth: 1,
    borderColor: DocColors.inputBorder,
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 12,
    backgroundColor: DocColors.surface,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    color: DocColors.textPrimary,
  },
  inputDisabled: {
    color: DocColors.textSecondary,
  },
  textarea: {
    height: undefined,
    minHeight: 116,
    paddingVertical: 8,
    lineHeight: 20,
    textAlignVertical: 'top',
  },

  // touchpoint source
  touchpoint: {
    borderLeftWidth: 4,
    borderLeftColor: DocColors.blue700,
    backgroundColor: DocColors.blue25,
    borderRadius: 6,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  touchpointTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.blue900,
  },
  touchpointText: {
    marginTop: 4,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },

  // resource card
  resourceCard: {
    borderWidth: 1,
    borderColor: DocColors.blue200,
    backgroundColor: DocColors.blue25,
    borderRadius: 10,
    padding: 16,
    gap: 10,
  },
  resourceTop: {
    flexDirection: 'row',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 10,
  },
  resourceCopy: {
    flex: 1,
    minWidth: 220,
  },
  resourceTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.blue900,
  },
  resourceDescription: {
    marginTop: 3,
    fontFamily: FontFamily.regular,
    fontSize: 12,
    lineHeight: 18,
    color: DocColors.textSecondary,
  },
  resourceLink: {
    minHeight: 40,
    paddingVertical: 8,
    paddingHorizontal: 20,
    borderRadius: 24,
    backgroundColor: DocColors.blue800,
    alignItems: 'center',
    justifyContent: 'center',
  },
  resourceLinkText: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textOnDark,
  },
  checkboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  checkboxLabel: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.textPrimary,
  },

  // notice card
  noticeCard: {
    borderWidth: 1,
    borderColor: '#FF8C09',
    backgroundColor: '#FEF9DA',
    borderRadius: 10,
    padding: 16,
  },
  noticeTitle: {
    fontFamily: FontFamily.bold,
    fontSize: 14,
    lineHeight: 20,
    color: DocColors.blue900,
  },
  noticeText: {
    marginTop: 6,
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 22,
    color: DocColors.textSecondary,
  },

  // validation
  validation: {
    borderWidth: 1,
    borderColor: '#FF8C09',
    backgroundColor: '#FCECDE',
    borderRadius: 9,
    paddingVertical: 12,
    paddingHorizontal: 14,
  },
  validationText: {
    fontFamily: FontFamily.regular,
    fontSize: 14,
    lineHeight: 20,
    color: '#4D2E0C',
  },
});
